const PROMOTION_TYPES = ['temporary_price', 'pack'];

const toNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const toInteger = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const parsed = Number.parseInt(value, 10);
  return Number.isInteger(parsed) ? parsed : null;
};

const normalizeIdList = (value) => {
  if (!Array.isArray(value)) return [];
  return Array.from(new Set(
    value
      .map((entry) => toInteger(entry))
      .filter((entry) => Number.isInteger(entry) && entry > 0)
  ));
};

export const resolvePromotionQuantityType = (metricUnit, quantity) => {
  const normalizedUnit = String(metricUnit || '').toLowerCase().trim();
  const qty = toNumber(quantity);
  const hasDecimals = Number.isFinite(qty) && Math.floor(qty) !== qty;
  if (normalizedUnit.includes('kg') || normalizedUnit.includes('kilo') || normalizedUnit.includes('gr')) {
    return 'weight';
  }
  if (normalizedUnit === 'g' || normalizedUnit === 'gr') {
    return 'weight';
  }
  if (normalizedUnit.includes('lt') || normalizedUnit.includes('litro') || normalizedUnit === 'l') {
    return 'other';
  }
  if (hasDecimals) return 'weight';
  return 'unit';
};

export const buildPromotionLineItem = ({
  product,
  variety,
  price,
  priceIndex,
  quantity,
  discount = 0,
  extra = {},
}) => ({
  varietyId: variety?.id,
  varietyPriceId: price?.id,
  varietyName: variety?.varietyName || null,
  productName: product?.productName || null,
  productImage: product?.productImage || null,
  varietyImage: variety?.varietyImage || null,
  priceImage: price?.priceImage || null,
  description: price?.description || null,
  priceDescription: price?.description || null,
  categoryId: product?.categoryId ?? null,
  categoryName: product?.Category?.name || null,
  metricUnit: price?.metricUnit || null,
  price: Number(price?.price || 0),
  discount: Number(discount || 0),
  quantity: Number(quantity || 0),
  quantityType: resolvePromotionQuantityType(price?.metricUnit, quantity),
  priceIndex,
  ...extra,
});

const isPromotionActiveForContext = (promotion, context) => {
  if (!promotion || promotion.isDeleted) return false;
  if (!PROMOTION_TYPES.includes(String(promotion.type || '').toLowerCase())) return false;
  if (String(promotion.status || 'active').toLowerCase() !== 'active') return false;
  if (context === 'quotation' && promotion.appliesToQuotations === false) return false;
  if (context !== 'quotation' && promotion.appliesToSales === false) return false;
  const now = Date.now();
  if (promotion.startsAt && new Date(promotion.startsAt).getTime() > now) return false;
  if (promotion.endsAt && new Date(promotion.endsAt).getTime() < now) return false;
  return true;
};

const computeBaseSubtotal = (item) => {
  const price = toNumber(item?.price) || 0;
  const quantity = toNumber(item?.quantity) || 0;
  if (quantity <= 0) return 0;
  return Math.max(Math.floor(price * quantity), 0);
};

const computeManualSubtotal = (item) => {
  const price = toNumber(item?.price) || 0;
  const discount = Math.max(
    toNumber(item?.manualDiscount) ?? toNumber(item?.discount) ?? 0,
    0
  );
  const quantity = toNumber(item?.quantity) || 0;
  if (quantity <= 0) return 0;
  return Math.max(Math.floor((price - discount) * quantity), 0);
};

const isUnitIntegerQuantity = (item) => {
  const quantity = toNumber(item?.quantity);
  if (!Number.isInteger(quantity) || quantity <= 0) return false;
  return String(item?.quantityType || 'unit').trim().toLowerCase() === 'unit';
};

const buildPromotionSnapshot = (promotion) => ({
  id: promotion.id,
  name: promotion.name,
  type: promotion.type,
  startsAt: promotion.startsAt || null,
  endsAt: promotion.endsAt || null,
  config: promotion.config || {},
});

const computePromotionSubtotal = (item, promotion) => {
  const config = promotion?.config || {};
  const varietyPriceId = toInteger(item?.varietyPriceId);
  const targets = normalizeIdList(config.varietyPriceIds);
  if (!Number.isInteger(varietyPriceId) || !targets.includes(varietyPriceId)) return null;

  const price = toNumber(item?.price) || 0;
  const quantity = toNumber(item?.quantity) || 0;
  if (price <= 0 || quantity <= 0) return null;

  if (promotion.type === 'temporary_price') {
    const promoUnitPrice = toInteger(config.promoUnitPrice);
    if (!Number.isInteger(promoUnitPrice) || promoUnitPrice <= 0) return null;
    return Math.max(Math.floor(promoUnitPrice * quantity), 0);
  }

  if (promotion.type === 'pack') {
    if (!isUnitIntegerQuantity(item)) return null;
    const packQuantity = toInteger(config.packQuantity);
    const packPrice = toInteger(config.packPrice);
    if (!Number.isInteger(packQuantity) || packQuantity <= 1) return null;
    if (!Number.isInteger(packPrice) || packPrice <= 0) return null;
    const packs = Math.floor(quantity / packQuantity);
    const remainder = quantity % packQuantity;
    if (packs <= 0) return null;
    return Math.max(Math.floor((packs * packPrice) + (remainder * price)), 0);
  }

  return null;
};

export const applyPromotionsToLineItems = (items = [], promotions = [], context = 'sale') => {
  const activePromotions = (Array.isArray(promotions) ? promotions : [])
    .filter((promotion) => isPromotionActiveForContext(promotion, context));

  return (Array.isArray(items) ? items : []).map((item) => {
    const baseSubtotal = computeBaseSubtotal(item);
    const manualDiscount = Math.max(
      toNumber(item?.manualDiscount) ?? toNumber(item?.discount) ?? 0,
      0
    );
    const sourceBeforePromotion = String(item?.discountSource || '').trim().toLowerCase();
    const baselineDiscountSource = sourceBeforePromotion === 'client_category' && manualDiscount > 0
      ? 'client_category'
      : manualDiscount > 0
        ? 'manual'
        : 'none';
    const manualSubtotal = computeManualSubtotal(item);
    let winner = null;

    activePromotions.forEach((promotion) => {
      const subtotal = computePromotionSubtotal(item, promotion);
      if (subtotal === null || subtotal >= manualSubtotal) return;
      if (!winner || subtotal < winner.subtotal || (
        subtotal === winner.subtotal && Number(promotion.id) < Number(winner.promotion.id)
      )) {
        winner = { promotion, subtotal };
      }
    });

    if (!winner) {
      return {
        ...item,
        manualDiscount,
        discount: manualDiscount,
        discountSource: baselineDiscountSource,
        promotionId: null,
        promotionName: null,
        promotionType: null,
        promotionDiscountAmount: 0,
        promotionSnapshot: null,
        subtotal: manualSubtotal,
      };
    }

    return {
      ...item,
      manualDiscount,
      discount: 0,
      discountAuthorizationId: null,
      discountSource: 'promotion',
      promotionId: winner.promotion.id,
      promotionName: winner.promotion.name,
      promotionType: winner.promotion.type,
      promotionDiscountAmount: Math.max(baseSubtotal - winner.subtotal, 0),
      promotionSnapshot: buildPromotionSnapshot(winner.promotion),
      subtotal: winner.subtotal,
    };
  });
};

export const computeLineItemsSubtotal = (items = []) =>
  (Array.isArray(items) ? items : []).reduce((sum, item) => {
    const subtotal = toNumber(item?.subtotal);
    if (Number.isFinite(subtotal) && subtotal >= 0) return sum + Math.floor(subtotal);
    return sum + computeManualSubtotal(item);
  }, 0);

export const computeOrderTotalsFromSubtotal = ({
  subtotal,
  orderDiscountAmount,
  orderDiscountPercentage,
}) => {
  const resolvedSubtotal = Math.max(Math.floor(Number(subtotal || 0)), 0);
  const percent = orderDiscountPercentage != null ? Number(orderDiscountPercentage) : null;
  const discountAmount = Number.isFinite(percent)
    ? Math.floor(resolvedSubtotal * (percent / 100))
    : Math.min(Math.max(Number(orderDiscountAmount || 0), 0), resolvedSubtotal);
  return {
    subtotal: resolvedSubtotal,
    total: Math.max(resolvedSubtotal - discountAmount, 0),
    discountAmount,
  };
};
